import { Controller } from "@hotwired/stimulus";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(...registerables, ChartDataLabels);

export default class extends Controller {
  static targets = ["canvas"];
  static values = {
    labels: Array,
    revenueData: Array,
    profitRateData: Array,
    revenueMax: Number,
  };

  connect() {
    this._currentIsMobile = window.innerWidth < 992;
    this._handleResize = this._handleResize.bind(this);
    this._resizeTimer = null;
    window.addEventListener("resize", this._handleResize, { passive: true });
    this.renderChart();
  }

  _handleResize() {
    if (this._resizeTimer) window.clearTimeout(this._resizeTimer);

    // リサイズ中の連続発火を抑制し、ブレークポイント跨ぎだけ再描画する
    this._resizeTimer = window.setTimeout(() => {
      const isMobileNow = window.innerWidth < 992;

      if (isMobileNow !== this._currentIsMobile) {
        this._currentIsMobile = isMobileNow;
        if (this.chart) this.chart.destroy();
        this.renderChart();
        return;
      }

      // ブレークポイント内のサイズ変更はChart.jsのリサイズに任せる
      if (this.chart) this.chart.resize();
    }, 150);
  }

  renderChart() {
    const ctx = this.canvasTarget.getContext("2d");
    const isMobile = window.innerWidth < 992;

    const labels = this.labelsValue;
    const salesData = this.revenueDataValue;
    const profitRateData = this.profitRateDataValue;
    const hasRevenue = salesData.length > 0 && salesData.every((v) => v !== 0);
    const hasProfitRate = profitRateData.length > 0 && profitRateData.every((v) => v !== 0);
    const ceilToNice = (value) => {
      if (!Number.isFinite(value) || value <= 0) return 0;
      const exponent = Math.floor(Math.log10(value));
      const fraction = value / Math.pow(10, exponent);
      const niceFraction =
        fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
      return niceFraction * Math.pow(10, exponent);
    };

    const salesMaxRaw = salesData.length ? Math.max(...salesData) : 0;
    const salesMinRaw = salesData.length ? Math.min(...salesData) : 0;
    const revenueCeilingBase = Math.max(this.revenueMaxValue || 0, salesMaxRaw);
    const revenueMaxTarget = revenueCeilingBase * 1.2;
    const revenueTickStep = ceilToNice(revenueMaxTarget / 4);
    const revenueMax = revenueTickStep ? revenueTickStep * 4 : 0;
    const revenueMin = salesMinRaw < 0
      ? Math.floor((salesMinRaw * 1.2) / revenueTickStep) * revenueTickStep
      : 0;

    const profitRateMaxRaw = profitRateData.length ? Math.max(...profitRateData) : 0;
    const profitRateMinRaw = profitRateData.length ? Math.min(...profitRateData) : 0;
    const profitRateMax = Math.max(
      35,
      Math.ceil((profitRateMaxRaw * 1.2) / 5) * 5
    );
    const profitRateMin = profitRateMinRaw < 0
      ? Math.floor((profitRateMinRaw * 1.2) / 5) * 5
      : 0;

    const firstLabel = labels[0] || "";
    const lastLabel = labels[labels.length - 1] || "";
    const titleParts = [
      hasRevenue ? "売上" : "",
      hasProfitRate ? "営業利益率" : "",
    ].filter(Boolean).join("・");
    const titleText = `${firstLabel}〜${lastLabel}の${titleParts}`;

    const unitLabelPlugin = {
      id: "unitLabel",
      beforeDraw: (chart) => {
        const ctx = chart.ctx;
        ctx.save();
        ctx.font = "8px sans-serif";
        ctx.fillStyle = "#333";
        ctx.textAlign = "left";
        if (isMobile) {
          ctx.fillText("単位（億円）", chart.chartArea.left- 30, chart.chartArea.top - 20);
        } else {
          ctx.fillText(
            "単位（億円）",
            chart.chartArea.left - 30,
            chart.chartArea.top - 20
          );
        }
        ctx.restore();
      },
    };

    const legendOffsetPlugin = {
      id: "legendOffset",
      beforeInit: (chart, _args, opts) => {
        const bottom = opts?.bottom || 0;
        if (!bottom || !chart.legend?.fit) return;

        const originalFit = chart.legend.fit;
        chart.legend.fit = function fit() {
          originalFit.bind(this)();
          this.height += bottom;
        };
      },
    };

    const chartAreaBorderPlugin = {
      id: "chartAreaBorder",
      afterDraw: (chart, _args, opts) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        const left = chartArea.left - 0.5;
        const top = chartArea.top - 0.5;
        const width = chartArea.right - chartArea.left + 1;
        const height = chartArea.bottom - chartArea.top + 1;

        ctx.save();
        ctx.strokeStyle = opts?.color || "#E5E5E5";
        ctx.lineWidth = opts?.lineWidth || 1;
        ctx.strokeRect(left, top, width, height);
        ctx.restore();
      },
    };

    this.chart = new Chart(ctx, {
      type: "bar",
      plugins: [unitLabelPlugin, legendOffsetPlugin, chartAreaBorderPlugin],
      data: {
        labels,
        datasets: [
          ...(hasRevenue
            ? [
                {
                  type: "bar",
                  label: "売上高",
                  data: salesData,
                  backgroundColor: "#A9E285",
                  borderRadius: 0,
                  barPercentage: 0.46,
                  order: 2,
                  yAxisID: "y",
                  datalabels: {
                    display: false,
                  },
                },
              ]
            : []),
          ...(hasProfitRate
            ? [
                {
                  type: "line",
                  label: "営業利益率",
                  data: profitRateData,
                  borderColor: "#B8A17A",
                  backgroundColor: "#B8A17A",
                  tension: 0.3,
                  pointRadius: 5,
                  pointBackgroundColor: "#FFFFFF",
                  pointBorderColor: "#C39750",
                  pointBorderWidth: 2,
                  order: 1,
                  yAxisID: "y1",
                  datalabels: {
                    display: true,
                    align: "top",
                    anchor: "end",
                    offset: 4,
                    color: "#333",
                    font: {
                      size: 10,
                    },
                    formatter: (value) => `${value}%`,
                  },
                },
              ]
            : []),
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: titleText,
            font: {
              size: isMobile ? 12 : 14,
              weight: "normal",
            },
            color: "#333",
            padding: {
              top: 4,
              bottom: isMobile ? 6 : 8,
            },
          },
          legend: {
            position: "bottom",
            reverse: true,
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              boxWidth: isMobile ? 10 : 10,
              boxHeight: isMobile ? 7 : 7,
              pointStyleWidth: isMobile ? 10 : 10,
              padding: isMobile ? 10 : 12,
              font: {
                size: isMobile ? 12 : 13,
              },
            },
          },
          legendOffset: {
            bottom: -10,
          },
          chartAreaBorder: {
            color: "#E5E5E5",
            lineWidth: 1,
          },
          tooltip: {
            callbacks: {
              label: function (context) {
                const label = context.dataset.label || "";
                const value = context.parsed.y;
                if (label === "売上高") {
                  return `${label}: ${value}億円`;
                }
                return `${label}: ${value}%`;
              },
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: true,
              color: "#E5E5E5",
              drawTicks: false,
            },
            ticks: {
              padding: 8,
            },
            border: {
              display: true,
              color: "#E5E5E5",
            },
          },
          y: {
            type: "linear",
            position: "left",
            display: hasRevenue,
            min: revenueMin,
            max: revenueMax,
            ticks: {
              stepSize: revenueTickStep || undefined,
              padding: 8,
              callback: (value) => value,
            },
            grid: {
              color: "#E5E5E5",
              drawTicks: false,
            },
            border: {
              display: true,
              color: "#E5E5E5",
            },
          },
          y1: {
            type: "linear",
            position: "right",
            min: profitRateMin,
            max: profitRateMax,
            display: hasProfitRate && !hasRevenue,
            grid: {
              drawOnChartArea: false,
            },
          },
        },
      },
    });
  }

  disconnect() {
    window.removeEventListener("resize", this._handleResize);
    if (this._resizeTimer) window.clearTimeout(this._resizeTimer);
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
